import React, {useEffect, useState} from "react";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { parseISO } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {DialogActions, DialogContent, DialogTitle, FormHelperText} from "@mui/material";
import Dialog from '@mui/material/Dialog';
import Grid2 from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import ReplayIcon from '@mui/icons-material/Replay';
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup"
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TextField from "@mui/material/TextField";
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {useForm} from "react-hook-form";
import {useCitepayUser, useAuthToken, useAgencies, useRequestUrl, useCurrentUser} from "../context/contextHooks";
import Select from "@mui/material/Select";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {updateUser} from "./updateUser";
import API from "../http/API";
import IconButton from "@mui/material/IconButton";
import {useSxStyles as sx} from "../styles/sxStyles";


const UserEdit = (props) => {
    const [open, setOpen] = React.useState(false)
    const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm();
    const editUser = props.editUser ? props.editUser : null;
    const callback = props.callback;
    const setLoading = props.setLoading;
    const terminals = props.terminals;
    const locations = props.locations;
    const citepayUser = useCitepayUser();
    const [authToken,] = useAuthToken();
    const [requestUrl,] = useRequestUrl();
    const [agencies,] = useAgencies();
    const [showPassword, setShowPassword] = useState(false);
    const [agencyDisabled, setAgencyDisabled] = useState(false);
    const [currentUser,] = useCurrentUser();
    const [pwdReset, setPwdReset] = useState(null);

    const handleOpen = () => {
        if (editUser) {
            if (editUser.lastPasswordReset) {
                setPwdReset(parseISO(editUser.lastPasswordReset));
            } else {
                setPwdReset(null);
            }
            setValue('username', editUser.username);
            setValue('password', '');
            setValue('agency', editUser.agencyId ? editUser.agencyId : '-1');
            setValue('firstName', editUser.firstName);
            setValue('middleName', editUser.middleName);
            setValue('lastName', editUser.lastName);
            setValue('defaultTerminal', editUser.defaultTerminal ? editUser.defaultTerminal : '-1');
            setValue('location', editUser.location ? editUser.location : (locations ? '-1' : ''));
            setValue('phone', editUser.phone);
            setValue('notes', editUser.notes);
            setValue('externalId', editUser.externalId);
        } else {
            setPwdReset(null);
            setValue('username', '');
            setValue('password', '');
            setValue('agency', '-1');
            setValue('firstName', '');
            setValue('middleName', '');
            setValue('lastName', '');
            setValue('defaultTerminal', '-1');
            setValue('location', locations ? '-1' : '');
            setValue('phone', '');
            setValue('notes', '');
            setValue('externalId', '');
        }
        setCheckboxState({enabled: (editUser ? editUser.enabled : false),
            accountLocked: (editUser ? editUser.accountLocked : false),
            passwordExpired: (editUser ? editUser.passwordExpired : false),
            userRole: (editUser ? editUser.isUser : false),
            adminRole: (editUser ? editUser.isAdmin : false),
            jsiRole: (editUser ? editUser.isJsi : false)})
        setOpen(true);
    }

    const handleClose = () => {
        reset(editUser);
        setOpen(false);
    }

    const togglePassword = () => {
        setShowPassword(!showPassword);
    }

    const disableMouseDown = (e) => {
        e.preventDefault();
    }

    const saveUser = (formRequest) => {
        if (currentUser.isAdmin && currentUser.agencyId) {
            formRequest.agency = currentUser.agencyId;
        }
        const userOut = updateUser(formRequest, checkboxState, pwdReset, editUser);
        const data = JSON.stringify(userOut);
        setLoading(true);
        API.postRetrieve(`${requestUrl}/users/saveUser`, data, 'editUser', `Bearer ${authToken}`, callback);
        handleClose();
    }

    const [checkboxState, setCheckboxState] = useState({
        enabled: (editUser ? editUser.enabled : false),
        accountLocked: (editUser ? editUser.accountLocked : false),
        passwordExpired: (editUser ? editUser.passwordExpired : false),
        userRole: (editUser ? editUser.isUser : false),
        adminRole: (editUser ? editUser.isAdmin : false),
        jsiRole: (editUser ? editUser.isJsi : false),
    })

    const handleCheckbox = (e) => {
        if (e.target.checked && (e.target.name === 'userRole' || e.target.name === 'adminRole' ||  e.target.name === 'jsiRole')) {
            setCheckboxState(Object.assign({}, checkboxState, {
                userRole: (e.target.name === 'userRole'),
                adminRole: (e.target.name === 'adminRole'),
                jsiRole: (e.target.name === 'jsiRole')
            }));
        } else {
            setCheckboxState({ ...checkboxState, [e.target.name]: e.target.checked });
        }
    }

    useEffect(() => {
        if (checkboxState.jsiRole) {
            setAgencyDisabled(true);
            setValue('agency', '', { shouldValidate: true });
        } else {
            setAgencyDisabled(false);
        }
    }, [checkboxState])

    return <>
        {editUser ?
            <EditIcon color='primary' onClick={handleOpen}/>
            :
            <Button variant="contained"
                    color="primary"
                    startIcon={<PersonAddOutlinedIcon/>}
                    sx={{...sx.dialog, margin: '1rem', backgroundColor: 'palette.primary'}}
                    onClick={handleOpen}
            >
                Add User
            </Button>
        }
        {open ?
            <Dialog
                disableEscapeKeyDown
                fullWidth
                maxWidth='md'
                open={open}
                onClose={handleClose}
                sx={sx.dialog}
            >
                <DialogTitle style={{paddingBottom: '0'}}>
                    <Typography variant="h5" color='primary'>
                        {editUser ?
                            `Edit User: ${editUser.firstName} ${editUser.lastName} (${editUser.username})`
                            : 'New User'
                        }
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{mT: '0', pT: '0'}}>
                    <Grid2 container >
                        <Grid2 size={12}>
                            <FormControl fullWidth component="fieldset" sx={sx.formControl}>
                                <Grid2 container spacing={2} alignItems='top' >
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            required
                                            {...register('username')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>Email</FormLabel>
                                            <TextField
                                                id="username"
                                                name="username"
                                                variant='outlined'
                                                autoComplete="off"
                                                required={!editUser}
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                            </InputAdornment>
                                                        )
                                                    },

                                                    htmlInput: {readOnly: (!!editUser), maxLength: 100, sx: sx.textbox}
                                                }} />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            required
                                            {...register('password')}
                                        >
                                            <FormLabel shrink={true} htmlFor="standard-adornment-password" sx={sx.inputLabel}>Password</FormLabel>
                                            <TextField
                                                id="standard-adornment-password"
                                                name="password"
                                                variant='outlined'
                                                autoComplete="off"
                                                type={showPassword ? 'text' : 'password'}
                                                maxLength="100"
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={togglePassword}
                                                                    onMouseDown={disableMouseDown}
                                                                    size="large">
                                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    },

                                                    htmlInput: {sx: sx.textbox}
                                                }} />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        {citepayUser ?
                                            <FormControl fullWidth
                                                         {...register('agency')}>
                                                <FormLabel shrink={true} id="agency-label" sx={sx.inputLabel} required={true}>Select an Agency</FormLabel>
                                                <Select
                                                    name='agency'
                                                    fullWidth
                                                    sx={{height: '2rem'}}
                                                    variant='outlined'
                                                    native
                                                    disabled={agencyDisabled}
                                                    labelId="agency-label"
                                                    inputProps={{
                                                        defaultValue: (editUser ? editUser.agencyId : '-1')
                                                    }}>
                                                    <option key="-1" value=''></option>
                                                    {agencies && agencies.length && agencies.map(agency => {
                                                        return (
                                                            <option key={agency.key} value={agency.value}>{agency.text}</option>
                                                        );
                                                    })}
                                                </Select>
                                                { errors.agency ?
                                                    <FormHelperText>Agency is required</FormHelperText> : <span>&nbsp;</span>
                                                }

                                            </FormControl>
                                            :
                                            <span>&nbsp;</span>
                                        }
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            required
                                            {...register('firstName')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>First Name</FormLabel>
                                            <TextField
                                                id="firstName"
                                                name="firstName"
                                                variant='outlined'
                                                autoComplete="off"
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: {maxLength: 100, sx: sx.textbox}
                                                }}

                                            />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            {...register('middleName')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>Middle Name</FormLabel>
                                            <TextField
                                                id="middleName"
                                                name="middleName"
                                                variant='outlined'
                                                autoComplete="off"
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: {maxLength: 100, sx: sx.textbox}
                                                }}

                                            />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            required
                                            {...register('lastName')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>Last Name</FormLabel>
                                            <TextField
                                                id="lastName"
                                                name="lastName"
                                                variant='outlined'
                                                autoComplete="off"
                                                required
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: {maxLength: 100, sx: sx.textbox}
                                                }}

                                            />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            {...register('phone')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>Phone Number</FormLabel>
                                            <TextField
                                                id="phone"
                                                name="phone"
                                                variant='outlined'
                                                autoComplete="off"
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: {maxLength: 50, sx: sx.textbox}
                                                }}

                                            />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <FormControl
                                            fullWidth
                                            {...register('externalId')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>External ID</FormLabel>
                                            <TextField
                                                id="externalId"
                                                name="externalId"
                                                variant='outlined'
                                                autoComplete="off"
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: {maxLength: 100, sx: sx.textbox}
                                                }}

                                            />
                                        </FormControl>
                                    </Grid2>
                                    { terminals && terminals.length > 0 &&
                                        <Grid2 size={4}>
                                            <FormControl fullWidth
                                                         {...register('defaultTerminal')}>
                                                <FormLabel shrink={true} id="terminal-label" sx={sx.inputLabel}>Default Terminal</FormLabel>
                                                <Select
                                                    name='defaultTerminal'
                                                    fullWidth
                                                    sx={{height: '2rem'}}
                                                    variant='outlined'
                                                    native
                                                    disabled={agencyDisabled}
                                                    labelId="terminal-label"
                                                    inputProps={{
                                                        defaultValue: (editUser && editUser.defaultTerminal ? editUser.defaultTerminal : '-1')
                                                    }}>
                                                    <option key="-1" value='-1'>None Selected</option>
                                                    { terminals.map(term => {
                                                        return (
                                                            <option key={term.laneId} value={term.laneId}>{term.terminalId}</option>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </Grid2>
                                    }
                                    <Grid2 size={4}>
                                        <FormControl
                                            name='location'
                                            fullWidth
                                            {...register('location')}
                                        >
                                            <FormLabel shrink={true} sx={sx.inputLabel}>Location</FormLabel>
                                            { locations && locations.length > 0 ?
                                                <Select
                                                    name='location'
                                                    fullWidth
                                                    sx={{height: '2rem'}}
                                                    variant='outlined'
                                                    native
                                                >
                                                    <option key="-1" value='-1'>None Selected</option>
                                                    { locations.map(loc => {
                                                        return (
                                                            <option key={loc.name} value={loc.name}>{loc.displayName}</option>
                                                        );
                                                    })}
                                                </Select> :
                                                <TextField
                                                    id="location"
                                                    name="location"
                                                    variant='outlined'
                                                    autoComplete="off"
                                                    fullWidth
                                                    slotProps={{
                                                        htmlInput: {maxLength: 100, sx: sx.textbox}
                                                    }}

                                                />
                                            }
                                        </FormControl>
                                    </Grid2>
                                    { citepayUser &&
                                        <Grid2 style={{paddingTop: '.9rem'}} size={4}>
                                            <FormControl fullWidth sx={sx.inputLabel}>
                                                <FormLabel shrink={true} sx={sx.inputLabel}>
                                                    Last Password Reset
                                                </FormLabel>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        name='pwdReset'
                                                        disableMaskedInput={false}
                                                        inputFormat='MM/dd/yyyy'
                                                        defaultValue={null}
                                                        value={pwdReset}
                                                        onChange={(newValue) => {
                                                            setPwdReset(newValue)
                                                        }}
                                                        inputProps={{sx: sx.textbox}}
                                                        renderInput={(params) =>
                                                            <TextField
                                                                {...params}
                                                                variant='outlined'
                                                            />
                                                        }
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Grid2>
                                    }
                                    <Grid2 size={12}>
                                        <FormControl
                                            fullWidth
                                            {...register('notes')}
                                        >
                                            <FormLabel
                                                for='notes'
                                                sx={sx.inputLabel}
                                            >
                                            </FormLabel>
                                            <TextField
                                                id='notes'
                                                label='Notes'
                                                aria-label='Notes'
                                                name='notes'
                                                fullWidth
                                                multiline
                                                inputComponent='textarea'
                                                slotProps={{
                                                    htmlInput: {
                                                        rows: 6,
                                                        style: {height: '15vh', resize: 'none'}
                                                    },

                                                    inputLabel: {htmlFor: 'notes', sx: sx.inputLabel}
                                                }} />
                                        </FormControl>
                                    </Grid2>
                                    <Grid2 size={12}>
                                        <Grid2 container spacing={2}>
                                            <Grid2>
                                                <FormLabel component="legend">
                                                    <Typography variant="h6" color='primary'>
                                                        Security Settings
                                                    </Typography>
                                                </FormLabel>
                                                <FormGroup row>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="enabled"
                                                                color='primary'
                                                                defaultChecked={(editUser ? editUser.enabled : false)}
                                                                onChange={handleCheckbox}
                                                            />
                                                        }
                                                        label="Enabled"
                                                        sx={sx.inputLabel}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="accountLocked"
                                                                color='primary'
                                                                checked={checkboxState.accountLocked}
                                                                onChange={handleCheckbox}
                                                            />
                                                        }
                                                        label="Account Locked"
                                                        sx={sx.inputLabel}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="passwordExpired"
                                                                color='primary'
                                                                checked={checkboxState.passwordExpired}
                                                                onChange={handleCheckbox}
                                                            />
                                                        }
                                                        label="Password Expired"
                                                        sx={sx.inputLabel}
                                                    />
                                                </FormGroup>
                                            </Grid2>
                                            <Grid2>
                                                <FormLabel component="legend">
                                                    <Typography variant="h6" color='primary'>
                                                        Security Roles
                                                    </Typography>
                                                </FormLabel>
                                                <FormGroup row>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="userRole"
                                                                color='primary'
                                                                checked={checkboxState.userRole}
                                                                onChange={handleCheckbox}
                                                            />
                                                        }
                                                        label="User"
                                                        sx={sx.inputLabel}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="adminRole"
                                                                color='primary'
                                                                checked={checkboxState.adminRole}
                                                                onChange={handleCheckbox}
                                                            />
                                                        }
                                                        label="Admin"
                                                        sx={sx.inputLabel}
                                                    />
                                                    { citepayUser ?
                                                        <span>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        name="jsiRole"
                                                                        color='primary'
                                                                        checked={checkboxState.jsiRole}
                                                                        onChange={handleCheckbox}
                                                                    />
                                                                }
                                                                label="JSI"
                                                                sx={sx.inputLabel}
                                                            />
                                                            </span>
                                                        : null
                                                    }
                                                </FormGroup>
                                            </Grid2>
                                        </Grid2>
                                    </Grid2>
                                </Grid2>
                            </FormControl>
                        </Grid2>
                    </Grid2>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined"
                            color="primary"
                            startIcon={<ReplayIcon/>}
                            sx={sx.button}
                            onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained"
                            color="primary"
                            startIcon={<SaveAltIcon/>}
                            sx={sx.button}
                            onClick={handleSubmit(saveUser)}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            :
            null
        }
    </>;
};

export default UserEdit;
